// Locks the page behind a modal on touch devices.
//
// Bootstrap locks the background by putting `overflow: hidden` on <body>, which
// iOS Safari ignores. The page therefore keeps scrolling underneath an open
// modal, and — more damagingly — when the on-screen keyboard opens, Safari
// scrolls the visual viewport to reveal the focused field while the modal, being
// `position: fixed`, keeps painting against the unmoved layout viewport. Touch
// coordinates are then offset from what is on screen: a control ignores a tap on
// itself and responds to a tap below it. In landscape the keyboard covers ~40%
// of the height, so the offset is large enough to make the last field of a tall
// modal effectively untappable.
//
// Pinning <body> at its current offset removes the layout/visual viewport
// divergence, because there is nothing left for Safari to scroll.
//
// Scoped to coarse pointers: desktop does not have the bug, and pinning <body>
// there would cost the (correctly behaving) background scroll position on every
// modal open.
import { useEffect } from "react";

export function useTouchScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!window.matchMedia?.("(pointer: coarse)").matches) return undefined;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      // `position: fixed` discards the scroll offset, so put it back rather
      // than dropping the user at the top of the calendar on every close.
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

export default useTouchScrollLock;
